import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useCallback } from 'react'

import { useIsMounted } from '../../hooks'

import { AriaLiveContext } from './AriaLive.context'
import VisuallyHidden from 'hsds/components/visually-hidden'

const useAriaLiveCore = (timeout = 5000) => {
  const announcementTimeout = useRef()
  const [announcement, setAnnouncement] = useState(null)
  const [announcementType, setAnnouncementType] = useState('polite')
  const isMounted = useIsMounted()

  useEffect(() => {
    const latestAnnouncementCooldown = setTimeout(() => {
      isMounted() && setAnnouncement(null)
    }, timeout)

    return () => clearTimeout(latestAnnouncementCooldown)
  }, [announcement, isMounted, timeout])

  const ariaAnnounce = useCallback(
    (announcement, type = 'polite') => {
      setAnnouncement(null)
      setAnnouncementType(type)
      announcementTimeout.current = setTimeout(
        () => isMounted() && setAnnouncement(announcement),
        10
      )
    },
    [isMounted]
  )

  const contextValue = useMemo(
    () => ({
      ariaAnnounce,
    }),
    [ariaAnnounce]
  )

  useEffect(() => {
    return () => {
      if (announcementTimeout.current) clearTimeout(announcementTimeout.current)
    }
  }, [])

  return { announcement, announcementType, contextValue }
}

const AriaLive = ({ children }) => {
  const { announcement, announcementType, contextValue } = useAriaLiveCore()

  return (
    <AriaLiveContext.Provider value={contextValue}>
      {children}
      <VisuallyHidden>
        <div role="status" aria-live={announcementType}>
          {announcement}
        </div>
      </VisuallyHidden>
    </AriaLiveContext.Provider>
  )
}

AriaLive.propTypes = {
  children: PropTypes.node.isRequired,
}

export default AriaLive
